import { CommonNode, documentToReactComponents } from "@contentful/rich-text-react-renderer";
import * as RichTextTypes from "@contentful/rich-text-types";
import { BLOCKS, Document, INLINES, MARKS } from "@contentful/rich-text-types";
import Image from "next/image";
import Hyperlink from "components/Hyperlink";
import CodeSnippets from "components/CodeSnippets/CodeSnippets";
import ComponentStatisticPanel from "components/ComponentStatisticPanel/ComponentStatisticPanel";
import ConversionPanel from "components/ConversionPanel/ConversionPanel";
import ListItem from "components/ListItem/ListItem";
import Testimonial from "components/Testimonial";
import VideoComponent from "components/VideoComponent/VideoComponent";
import Embedded from "components/Embedded";
import Accordion from "components/Accordion/Accordion";
import captionRichTextParser from "utils/captionRichTextParser";
import type {
  ComponentCallToAction,
  ComponentCodeSnippetFragment,
  ComponentConversionPanelFragment,
  ComponentImage,
  ComponentListItemFragment,
  ComponentTestimonialFragment,
  ComponentVideoFragment,
  ComponentStatisticPanelFragment,
  ComponentLogoCloudFragment,
  ComponentHeadingFragment,
  ComponentEmbeddedFragment,
  ComponentFaqFragment,
} from "contentful/gql/graphql";
import type { ReactElement, ReactNode } from "react";
import { slug } from "github-slugger";
import CtaLink from "components/CtaLink";
import ComponentHeading from "components/ComponentHeading/ComponentHeading";
import richTextParser from "./richTextParser";
import { cn } from "./functions";
import ShareIcon from "components/Icons/ShareIcon";
import CheckIcon from "components/Icons/CheckIcon";
import CopyLinkButton from "components/CopyLinkButton";
export interface RichText {
  json: Document;
  links?: {
    assets: {
      block: Array<{
        __typename: "Asset";
        title?: string | null;
        fileName?: string | null;
        size?: number | null;
        url?: string | null;
        width?: number | null;
        height?: number | null;
        description?: string | null;
        sys?: { __typename?: "Sys"; id: string };
      } | null>;
    };
    entries: {
      block: Array<
        | ComponentCallToAction
        | ComponentTestimonialFragment
        | ComponentImage
        | ComponentCodeSnippetFragment
        | ComponentListItemFragment
        | ComponentVideoFragment
        | ComponentStatisticPanelFragment
        | ComponentConversionPanelFragment
        | ComponentLogoCloudFragment
        | ComponentHeadingFragment
        | ComponentTestimonialFragment
        | ComponentEmbeddedFragment
        | ComponentFaqFragment
      >;
    };
  };
}

function textFromNode(node: any) {
  return node?.content?.reduce((prev: any, content: any) => {
    if (content.nodeType === "text") {
      return prev.concat(content.value);
    } else if (content.content) {
      return prev.concat(textFromNode(content));
    }
    return prev + (content.value || "");
  }, "") as string;
}

const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const yOffset = -150; // 50px margin
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const HeadingWrapper = ({ id, className, children }: { id: string; className: string; children: ReactNode }) => {
  return (
    <div className={className}>
      {children}
      <CopyLinkButton id={id} />
    </div>
  );
};

const blogRichTextParser = (content: RichText) => {
  if (!content) {
    return null;
  }
  const { json, links } = content;

  const options = {
    renderText: (text: ReactNode) => (text ? <span dangerouslySetInnerHTML={{ __html: text }} /> : null),
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => <span className="font-bold">{text}</span>,
      [MARKS.ITALIC]: (text: ReactNode) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text: ReactNode) => (
        <span
          style={{
            borderRadius: "0.125rem",
            background: "linear-gradient(90deg, rgba(90, 43, 212, 0.10) 75.45%, rgba(18, 2, 47, 0.00) 104.67%)",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
          }}
          className="px-4xs py-[1px] font-medium text-primary-dark-800"
        >
          {text}
        </span>
      ),
      [MARKS.CODE]: (text: ReactNode) => (
        <code className="inline-block break-all rounded bg-primary-dark-100 px-2xs font-mono text-[90%] leading-snug">
          {text}
        </code>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => {
        const { content } = node as RichTextTypes.Paragraph;
        if (content.length === 1 && content[0].nodeType === "text" && content[0].value.trim() === "") {
          return null;
        }
        return <p className="my-xs leading-normal">{children}</p>;
      },
      [BLOCKS.HEADING_1]: (node: CommonNode, children: ReactNode) => {
        const text = textFromNode(node);
        const id = slug(text);
        return (
          <HeadingWrapper
            id={id}
            className={cn(
              "mb-sm mt-md font-display text-2xl font-bold leading-snug text-primary-dark-900 md:text-3xl xl:text-4xl",
              text.startsWith("[Hidden]") ? "invisible" : "",
            )}
          >
            <h1 className="inline" id={id}>
              {text.startsWith("[Hidden]") ? null : text}
            </h1>
          </HeadingWrapper>
        );
      },
      [BLOCKS.HEADING_2]: (node: CommonNode, children: ReactNode) => {
        const text = textFromNode(node);
        const id = slug(text);
        return (
          <HeadingWrapper
            id={id}
            className={cn(
              "mb-sm mt-md font-display text-xl font-bold leading-snug text-primary-dark-900 md:text-2xl xl:text-3xl",
              text.startsWith("[Hidden]") ? "invisible" : "",
            )}
          >
            <h2 className="inline" id={id}>
              {text.startsWith("[Hidden]") ? null : text}
            </h2>
          </HeadingWrapper>
        );
      },
      [BLOCKS.HEADING_3]: (node: CommonNode, children: ReactNode) => {
        const text = textFromNode(node);
        const id = slug(text);
        return (
          <HeadingWrapper
            id={id}
            className={cn(
              "mb-sm mt-md font-display text-lg font-bold text-primary-dark-900 md:text-xl xl:text-2xl",
              text.startsWith("[Hidden]") ? "invisible" : "",
            )}
          >
            <h3 className="inline" id={id}>
              {text.startsWith("[Hidden]") ? null : text}
            </h3>
          </HeadingWrapper>
        );
      },
      [BLOCKS.HEADING_4]: (node: CommonNode, children: ReactNode) => <h4>{children}</h4>,
      [BLOCKS.HEADING_5]: (node: CommonNode, children: ReactNode) => <h5>{children}</h5>,
      [BLOCKS.HEADING_6]: (node: CommonNode, children: ReactNode) => <h6>{children}</h6>,
      [BLOCKS.UL_LIST]: (node: CommonNode, children: ReactNode) => (
        <ul className="space-y-2xs pl-sm [&_li]:list-disc">{children}</ul>
      ),
      [BLOCKS.LIST_ITEM]: (node: CommonNode, children: ReactNode) => {
        const { content: nodeContent } = node as RichTextTypes.ListItem;
        if (
          nodeContent[0].nodeType === "embedded-entry-block" ||
          nodeContent[0].nodeType === "embedded-asset-block" ||
          nodeContent[0].nodeType === "blockquote"
        ) {
          return <li className="list-none">{children}</li>;
        }

        return <li className="[&_p]:inline">{children}</li>;
      },
      [BLOCKS.OL_LIST]: (node: CommonNode, children: ReactNode) => (
        <ol className="space-y-2xs pl-sm [&_li]:list-decimal">{children}</ol>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        const { id } = target.sys;
        const assets = links?.assets.block;
        const nestedAsset = assets?.filter((asset) => asset?.sys?.id === id);
        const [asset] = nestedAsset ?? [];

        if (
          asset?.url &&
          (asset?.url.includes("images.ctfassets.net") || asset?.url.includes("assets.ctfassets.net")) // Treat as image
        ) {
          return (
            <figure>
              <Image
                src={asset.url}
                alt={asset.description || "Blog image"}
                width={asset.width || "832"}
                height={asset.height || "520"}
                className="mx-auto my-xs h-auto w-full rounded-lg"
                quality={90}
                priority={false}
                loading="lazy"
              />
            </figure>
          );
        } else if (asset?.url && asset.url?.includes("videos.ctfassets.net")) {
          // Treat as video
          return (
            <video
              src={asset.url + "?fm=mp4"}
              className="my-sm h-auto w-full overflow-hidden rounded-xl"
              width="100%"
              height="auto"
              muted
              autoPlay
              loop
              playsInline
            >
              <source src={asset.url + "?fm=mp4"} type="video/mp4" />
            </video>
          );
        }
      },
      [BLOCKS.QUOTE]: (node: CommonNode, children: ReactNode) => (
        <blockquote className="relative">
          <span className="absolute left-1/2 top-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-light-500/20 blur-[100px] lg:h-1/2 lg:w-1/2"></span>
          <span className="absolute -left-xs top-sm text-[5rem] font-medium leading-none text-primary-light-50 lg:-left-2xs lg:-top-2xs lg:text-[13rem] 2xl:-left-md 2xl:-top-md 2xl:text-[17rem]">
            &#8220;
          </span>
          <span className="absolute -bottom-sm -right-xs text-[5rem] font-medium leading-none text-primary-light-50 lg:-bottom-xl lg:-right-2xs lg:text-[13rem] 2xl:-bottom-3xl 2xl:-right-md 2xl:text-[17rem]">
            &#8221;
          </span>
          <div className="relative z-10 px-sm py-sm text-justify text-base font-medium text-primary-dark-800 sm:text-lg lg:px-xl lg:py-lg lg:text-xl 2xl:text-2xl">
            {children}
          </div>
        </blockquote>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        const { id } = target.sys;
        const entries = links?.entries?.block;
        const nestedEntry = entries?.filter((entry) => entry?.sys?.id === id);
        if (!nestedEntry) {
          return null;
        }
        const [entry] = nestedEntry;

        switch (entry?.__typename) {
          case "ComponentCallToAction":
            return <CtaLink {...entry} />;
          case "ComponentTestimonial":
            return entry && <Testimonial {...entry} />;
          case "ComponentImage":
            const asset = entry?.imageDesktop;
            if (asset?.url) {
              return (
                entry?.imageDesktop?.url && (
                  <figure className="mx-auto my-xs">
                    <Image
                      src={asset.url}
                      alt={asset.description || "Image"}
                      width={asset.width || "832"}
                      height={asset.height || "520"}
                      className="h-auto w-full rounded-lg"
                      quality={90}
                      priority={false}
                      loading="lazy"
                    />
                    <figcaption className="mt-3xs font-bold leading-tight text-primary-light-500">
                      {entry.caption ? captionRichTextParser(entry.caption) : null}
                    </figcaption>
                  </figure>
                )
              );
            } else {
              return null;
            }

          case "ComponentConversionPanel":
            return (
              <div className="my-sm lg:my-md">
                <ConversionPanel {...entry} />
              </div>
            );
          case "ComponentCodeSnippet":
            return <CodeSnippets node={entry} />;
          case "ComponentVideo":
            return <VideoComponent {...entry} />;
          case "Embedded":
            return <Embedded embedded={entry} />;
          case "ComponentFaq":
            return (
              <div className="my-lg">
                <Accordion {...entry} />
              </div>
            );
          case "ComponentListItem":
            return <ListItem listItem={entry} />;
          case "ComponentStatisticPanel":
            return <ComponentStatisticPanel {...entry} />;
          case "ComponentHeading":
            return (
              <div className="translate-y-7">
                <ComponentHeading {...entry} />
              </div>
            );
          default:
            return null;
        }
      },
      [INLINES.HYPERLINK]: (node: CommonNode, children: ReactNode) => {
        const { data } = node;
        return (
          <Hyperlink href={data.uri} className="text-primary-light-500 underline-offset-2 hover:underline">
            {children}
          </Hyperlink>
        );
      },
      [BLOCKS.HR]: () => <hr className="my-sm" />,
      [BLOCKS.TABLE]: (node: CommonNode, children: ReactNode) => (
        <div className="my-xs text-xs md:text-sm">
          <table className="overflow-hidden rounded-lg bg-secondary-light-100">
            <tbody className="[&_td]:border-r [&_td]:border-r-primary-dark-900/20 [&_td]:p-2xs last:[&_td]:border-r-0 [&_th]:bg-primary-dark-900 [&_th]:p-2xs [&_th]:text-primary-neutral-50 [&_tr]:border-b [&_tr]:border-b-primary-dark-900/20 last:[&_tr]:border-b-0">
              {children}
            </tbody>
          </table>
        </div>
      ),
      [BLOCKS.TABLE_ROW]: (node: CommonNode, children: ReactNode) => <tr>{children}</tr>,
    },
  };

  return documentToReactComponents(json, options) as ReactElement[];
};

export default blogRichTextParser;
